/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ActionButton, IconButton } from '@fluentui/react/lib/Button';
import { DetailsList, IDetailsListProps, IDetailsListStyles } from '@fluentui/react/lib/DetailsList';
import { IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { IScrollablePaneStyleProps, IScrollablePaneStyles, ScrollablePane, ScrollbarVisibility } from '@fluentui/react/lib/ScrollablePane';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction, getId, SelectionMode } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { SearchResultsAnnouncer } from 'src/core/components/SearchResultsAnnouncer';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { logButtonClick, logButtonClickWithCustomData } from 'src/core/services/telemetry/eventsNamesTemplate';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { useQnaMetricActions } from 'src/qnaMaker/core/hooks/useQnaMetricActions';
import { QnaMakerMetadataType } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerLanguageDirection } from 'src/qnaMaker/core/types/QnaMakerLanguages';

type QnaMakerMetadataProps = {
    pairId?: number;
    readOnly?: boolean;
    isInEditMode?: boolean;
    showDeleteOption?: boolean;
    isAddActionDisabled: boolean;
    flattenMetadata?: Set<string>;
    metadata: QnaMakerMetadataType;
    areKeysShownInDropDown?: boolean;
    onChangeMetadata: (newMetadata: QnaMakerMetadataType) => void;
};

const messages = defineMessages({
    add: { id: 'qna.qnaMakerMetadata.add', defaultMessage: 'Add' },
    addAriaLabel: { id: 'qna.qnaMakerMetadata.addAriaLabel', defaultMessage: 'Add metadata' },
    newKeyPlaceholder: { id: 'qna.qnaMakerMetadata.newKeyPlaceholder', defaultMessage: 'Type metadata key...' },
    newValuePlaceholder: { id: 'qna.qnaMakerMetadata.newValuePlaceholder', defaultMessage: 'Type metadata value...' },
    deleteQuestion: { id: 'qna.qnaMakerMetadata.deleteQuestion', defaultMessage: 'Delete metadata' },
    dismissChanges: { id: 'qna.qnaMakerMetadata.dismissChanges', defaultMessage: 'Dismiss metadata' },
    submitChanges: { id: 'qna.qnaMakerMetadata.submitChanges', defaultMessage: 'Submit changes' },
    metadataDropDownPlaceholder: {
        id: 'qna.qnaMakerMetadata.metadataDropDownPlaceholder',
        defaultMessage: 'Select metadata'
    },
    emptyMetadataDropDownPlaceholder: {
        id: 'qna.qnaMakerMetadata.emptyMetadataDropDownPlaceholder',
        defaultMessage: 'No metadata available'
    },
    metadataKeyDropDown: {
        id: 'qna.qnaMakerMetadata.metadataKeyDropDown',
        defaultMessage: 'Metadata key dropdown'
    }
});

const submitIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({ root: { fontSize: '20px', lineHeight: '16px' } });
const deleteIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { fontSize: '12px', lineHeight: '12px', height: 'auto' }
});
const scrollableContainerStyles = classNamesFunction<IScrollablePaneStyleProps, IScrollablePaneStyles>()({
    contentContainer: { position: 'relative', maxHeight: '100%' },
    root: { minHeight: 0, position: 'relative', height: '100%', width: '100%' }
});
const suggestedDropDownStyles = (languageDirection: QnaMakerLanguageDirection) =>
    classNamesFunction<IDetailsListProps, IDetailsListStyles>()({
        root: {
            overflowY: 'auto',
            maxHeight: '150px',
            overflowX: 'hidden',
            direction: languageDirection,
            headerWrapper: { display: 'none' }
        }
    });

const SuggestedDropDown = (props: {
    items: string[];
    keyName: string;
    onItemClick: (item: string) => void;
    languageDirection: QnaMakerLanguageDirection;
}) => (
    <>
        <DetailsList
            items={props.items}
            isHeaderVisible={false}
            selectionMode={SelectionMode.none}
            columns={[
                {
                    minWidth: 100,
                    key: props.keyName,
                    name: props.keyName,
                    onRender: (suggestedItem: string) => (
                        <Stack onClick={() => props.onItemClick(suggestedItem)}>
                            <TruncatedText dir={props.languageDirection} maxWidth={200} title={suggestedItem}>
                                {suggestedItem}
                            </TruncatedText>
                        </Stack>
                    )
                }
            ]}
            styles={suggestedDropDownStyles(props.languageDirection)}
        />

        <SearchResultsAnnouncer isActive itemsLength={props.items.length} />
    </>
);

export const QnaMakerMetadata = observer((props: QnaMakerMetadataProps) => {
    const {
        pairId,
        metadata,
        showDeleteOption,
        readOnly = false,
        onChangeMetadata,
        isAddActionDisabled,
        isInEditMode = false,
        flattenMetadata = new Set(),
        areKeysShownInDropDown = false
    } = props;

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { serviceName } = getPortalFromUrl();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);
    const {
        resetRowMetrics,
        setTimeTakenToEditMetadata,
        increaseDeletedMetadataForPair,
        increaseAddedMetadataForPair,
        increaseMetadataClicksForPair,
        increaseTotalNumberOfClicksBeforeSave,
        getTimeTakenToAddOrDismissMetadataOutsideEditMode,
        setTimeTakenToAddOrDismissMetadataOutsideEditMode
    } = useQnaMetricActions();
    const { languageDirection, id: currentProjectId } = rootStore.currentQnaMakerProjectStore;

    const addButtonId = React.useRef(getId());
    const [isAdditionModeOn, setIsAdditionModeOn] = React.useState(false);

    const [isSuggestedMetadataKeyListShownPerIndex, setIsSuggestedMetadataKeyListShownPerIndex] = React.useState<{
        [index: number]: boolean;
    }>({});

    const [isSuggestedMetadataValueListShownPerIndex, setIsSuggestedMetadataValueListShownPerIndex] = React.useState<{
        [index: number]: boolean;
    }>({});

    const [{ newMetaDataKey, newMetaDataValue }, setNewMetadataRow] = useUpdateState({ newMetaDataKey: '', newMetaDataValue: '' });

    const metadataKeys = Object.keys(metadata);

    const suggestedMetadataKeys = React.useMemo(() => {
        const suggestedKeys: string[] = [];

        flattenMetadata.forEach(metadataEntry => {
            const suggestedKey = metadataEntry.split(':')[0].trim();
            if (!suggestedKeys.includes(suggestedKey)) {
                suggestedKeys.push(suggestedKey);
            }
        });

        return suggestedKeys;
    }, [flattenMetadata]);

    React.useEffect(() => {
        if (!isInEditMode) {
            setIsSuggestedMetadataKeyListShownPerIndex({});
            setIsSuggestedMetadataValueListShownPerIndex({});
        }
    }, [isInEditMode]);

    const addNewMetadata = () => {
        if (isInEditMode) {
            increaseMetadataClicksForPair(pairId);
        } else {
            increaseTotalNumberOfClicksBeforeSave();

            logButtonClickWithCustomData(serviceName, 'EditKb', 'AddMetadataOutsideEditMode', {
                pivotName: 'Qnas',
                projectId: currentProjectId,
                timeTakenToAddMetadata:
                    LuisConstants.QnaMaker.Metrics.addFieldBaseTimeInMilliseconds +
                    getTimeTakenToAddOrDismissMetadataOutsideEditMode(pairId),
                numberOfClicksToAddMetadata: LuisConstants.QnaMaker.Metrics.addOrDismissMetadataClickCounter
            });

            resetRowMetrics(pairId);
        }
        setIsAdditionModeOn(false);
        setNewMetadataRow({ newMetaDataKey: '', newMetaDataValue: '' });
        if (Boolean(newMetaDataKey) && Boolean(newMetaDataValue)) {
            onChangeMetadata({ ...metadata, [newMetaDataKey]: newMetaDataValue });
        }
    };

    const handleAddMetadataClick = () => {
        if (isInEditMode) {
            increaseMetadataClicksForPair(pairId);
            increaseAddedMetadataForPair(pairId);
        } else {
            increaseTotalNumberOfClicksBeforeSave();
        }

        logButtonClick(serviceName, 'EditKb', 'AddMetadata', 'Qnas', currentProjectId);

        if (!isAdditionModeOn) {
            setIsAdditionModeOn(true);

            return;
        }

        if (Boolean(newMetaDataKey) && Boolean(newMetaDataValue)) {
            setNewMetadataRow({ newMetaDataKey: '', newMetaDataValue: '' });
            onChangeMetadata({ ...metadata, [newMetaDataKey]: newMetaDataValue });
        }
    };

    const handleMetadataChange = (key: string, newKey: string, newValue: string) => {
        let newMetadata = Object.entries(metadata)
            .map(entry => {
                if (entry[0] === key) {
                    return [newKey, newValue];
                }

                return entry;
            })
            .reduce((acc, entry) => ({ ...acc, [entry[0]]: entry[1] }), {});

        if (Object.entries(newMetadata).findIndex(e => e[0] === key) === -1) {
            newMetadata = { ...newMetadata, [newKey]: newValue };
        }

        onChangeMetadata(newMetadata);
    };

    const handleMetadataDeletion = (key: string, index: number) => {
        if (isInEditMode) {
            increaseMetadataClicksForPair(pairId);
            increaseDeletedMetadataForPair(pairId);
        } else {
            increaseTotalNumberOfClicksBeforeSave();
        }

        updateSuggestedMetadataKeyListVisibilityForRow(index, false);
        updateSuggestedMetadataValueListVisibilityForRow(index, false);
        const newMetadata = Object.entries(metadata)
            .filter(entry => entry[0] !== key)
            .reduce((acc, entry) => ({ ...acc, [entry[0]]: entry[1] }), {});

        onChangeMetadata(newMetadata);
    };

    const handleChangeInAdditionRow = (_key: string, _value: string) => {
        setNewMetadataRow({ newMetaDataKey: _key, newMetaDataValue: _value });
    };

    const handleAdditionRowCanceling = () => {
        if (isInEditMode) {
            increaseMetadataClicksForPair(pairId);
        } else {
            increaseTotalNumberOfClicksBeforeSave();

            logButtonClickWithCustomData(serviceName, 'EditKb', 'CancelMetadataOutsideEditMode', {
                pivotName: 'Qnas',
                projectId: currentProjectId,
                timeTakenToCancelMetadata:
                    LuisConstants.QnaMaker.Metrics.addFieldBaseTimeInMilliseconds +
                    getTimeTakenToAddOrDismissMetadataOutsideEditMode(pairId),
                numberOfClicksToCancelMetadata: LuisConstants.QnaMaker.Metrics.addOrDismissMetadataClickCounter
            });

            resetRowMetrics(pairId);
        }
        setIsAdditionModeOn(false);
        setNewMetadataRow({ newMetaDataKey: '', newMetaDataValue: '' });
    };

    const updateSuggestedMetadataKeyListVisibilityForRow = (index: number, isVisible: boolean) => {
        if (isSuggestedMetadataKeyListShownPerIndex[index] !== isVisible) {
            const newState: { [index: number]: boolean } = {};
            newState[index] = isVisible;
            setIsSuggestedMetadataKeyListShownPerIndex(newState);
        }
    };

    const updateSuggestedMetadataValueListVisibilityForRow = (index: number, isVisible: boolean) => {
        if (isSuggestedMetadataValueListShownPerIndex[index] !== isVisible) {
            const newState: { [index: number]: boolean } = {};
            newState[index] = isVisible;
            setIsSuggestedMetadataValueListShownPerIndex(newState);
        }
    };

    const autoSelectElementWithId = (id: string) => {
        const element = document.getElementById(id);
        element?.focus();
    };

    const renderMetadataRowWithKeysDropDown = ({
        onDelete,
        onSelectionChange
    }: {
        onDelete: () => void;
        onSelectionChange: (newKey: string, newValue: string) => void;
    }) => {
        const filteredSuggestedMetadata: { key: string; text: string; data: string }[] = [];
        flattenMetadata.forEach(metadataEntry => {
            const suggestedKey = metadataEntry.split(':')[0].trim();
            const suggestedValue = metadataEntry.split(':')[1].trim();
            if (!metadataKeys.includes(suggestedKey)) {
                filteredSuggestedMetadata.push({
                    key: suggestedKey,
                    text: metadataEntry,
                    data: suggestedValue
                });
            }
        });

        return (
            <Stack horizontal tokens={{ childrenGap: 6 }}>
                <Stack
                    horizontal
                    verticalAlign="center"
                    aria-label={localize(messages.metadataKeyDropDown)}
                    tokens={{ childrenGap: 5 }}
                    styles={{ root: { width: '100%', marginLeft: '10px' } }}>
                    <LuisDropdown
                        options={filteredSuggestedMetadata}
                        styles={{ root: { width: '100%' } }}
                        aria-label={localize(messages.metadataKeyDropDown)}
                        placeholder={localize(
                            filteredSuggestedMetadata.length
                                ? messages.metadataDropDownPlaceholder
                                : messages.emptyMetadataDropDownPlaceholder
                        )}
                        onChange={(_, i) => onSelectionChange(i.key as string, i.data as string)}
                    />
                </Stack>

                <Stack horizontal>
                    <LuisTooltip content={localize(messages.submitChanges)}>
                        <IconButton
                            onClick={addNewMetadata}
                            aria-label={localize(messages.submitChanges)}
                            disabled={!newMetaDataKey || !newMetaDataValue}
                            iconProps={{ iconName: 'CheckMark', styles: submitIconStyles }}
                        />
                    </LuisTooltip>
                    {showDeleteOption && (
                        <LuisTooltip content={localize(messages.dismissChanges)}>
                            <IconButton
                                onClick={onDelete}
                                iconProps={{
                                    iconName: 'ChromeClose',
                                    styles: deleteIconStyles
                                }}
                                aria-label={localize(messages.dismissChanges)}
                            />
                        </LuisTooltip>
                    )}
                </Stack>
            </Stack>
        );
    };

    const renderMetadataRow = ({
        key,
        value,
        index,
        onDelete,
        onKeyChange,
        onValueChange,
        onSuggestedKeySelected,
        onSuggestedValueSelected,
        isAdditionRow = false
    }: {
        key: string;
        value: string;
        index: number;
        onDelete: () => void;
        isAdditionRow?: boolean;
        onKeyChange: (newKey: string) => void;
        onValueChange: (newValue: string) => void;
        onSuggestedKeySelected: (newKey: string) => void;
        onSuggestedValueSelected: (newKey: string) => void;
    }) => {
        const filteredSuggestedMetadataKeys = substringSearchStrategy(suggestedMetadataKeys, key).filter(
            suggestedKey => !metadataKeys.includes(suggestedKey)
        );

        let filteredSuggestedMetadataValues: string[] = [];
        flattenMetadata.forEach(metadataEntry => {
            const suggestedKey = metadataEntry.split(':')[0].trim();
            const suggestedValue = metadataEntry.split(':')[1].trim();
            if (suggestedKey === key) {
                filteredSuggestedMetadataValues.push(suggestedValue);
            }
        });
        filteredSuggestedMetadataValues = substringSearchStrategy(filteredSuggestedMetadataValues, value);

        const valueFieldId = getId();

        const handleOnTakenTimeChange = (time: number) => {
            if (isInEditMode) {
                setTimeTakenToEditMetadata(pairId, time);
            } else {
                setTimeTakenToAddOrDismissMetadataOutsideEditMode(pairId, time);
            }
        };

        const handleKeyDown = (event: React.KeyboardEvent) => {
            switch (event.key) {
                case LuisKeyCodes.Enter:
                    if (key && value && isAdditionRow) {
                        addNewMetadata();
                        event.preventDefault();
                        updateSuggestedMetadataKeyListVisibilityForRow(index, false);
                        updateSuggestedMetadataValueListVisibilityForRow(index, false);
                        document.getElementById(addButtonId.current)?.focus();
                    }
                    break;
                case LuisKeyCodes.Escape:
                    if (isAdditionRow) {
                        onDelete();
                        document.getElementById(addButtonId.current)?.focus();
                    }
                    break;
                default:
            }
        };

        return (
            <>
                <Stack key={index} horizontal tokens={{ childrenGap: 6 }}>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                        <LuisTextField
                            value={key}
                            dir={languageDirection}
                            onKeyDown={handleKeyDown}
                            onTimeTakenChange={handleOnTakenTimeChange}
                            onChange={(_, newVal) => onKeyChange(newVal)}
                            placeholder={localize(messages.newKeyPlaceholder)}
                            onMouseUp={() => {
                                updateSuggestedMetadataKeyListVisibilityForRow(index, true);
                                updateSuggestedMetadataValueListVisibilityForRow(index, false);
                            }}
                        />
                        <LuisText>:</LuisText>
                        <LuisTextField
                            value={value}
                            id={valueFieldId}
                            dir={languageDirection}
                            onKeyDown={handleKeyDown}
                            onTimeTakenChange={handleOnTakenTimeChange}
                            onChange={(_, newVal) => onValueChange(newVal)}
                            placeholder={localize(messages.newValuePlaceholder)}
                            onMouseUp={() => {
                                updateSuggestedMetadataKeyListVisibilityForRow(index, false);
                                updateSuggestedMetadataValueListVisibilityForRow(index, true);
                            }}
                        />
                    </Stack>

                    <Stack horizontal>
                        {isAdditionRow && (
                            <LuisTooltip content={localize(messages.submitChanges)}>
                                <IconButton
                                    disabled={!key || !value}
                                    aria-label={localize(messages.submitChanges)}
                                    iconProps={{ iconName: 'CheckMark', styles: submitIconStyles }}
                                    onClick={() => {
                                        addNewMetadata();
                                        updateSuggestedMetadataKeyListVisibilityForRow(index, false);
                                        updateSuggestedMetadataValueListVisibilityForRow(index, false);
                                    }}
                                />
                            </LuisTooltip>
                        )}
                        {(isInEditMode || showDeleteOption || isAdditionModeOn) && (
                            <LuisTooltip content={localize(messages.dismissChanges)}>
                                <IconButton
                                    onClick={onDelete}
                                    aria-label={localize(messages.dismissChanges)}
                                    iconProps={{
                                        iconName: isAdditionModeOn ? 'ChromeClose' : 'Delete',
                                        styles: isAdditionModeOn ? deleteIconStyles : undefined
                                    }}
                                />
                            </LuisTooltip>
                        )}
                    </Stack>
                </Stack>

                {isSuggestedMetadataKeyListShownPerIndex[index] && filteredSuggestedMetadataKeys.length > 0 && (
                    <SuggestedDropDown
                        keyName={'keysColumn'}
                        languageDirection={languageDirection}
                        items={filteredSuggestedMetadataKeys}
                        onItemClick={suggestedKey => {
                            onSuggestedKeySelected(suggestedKey);
                            autoSelectElementWithId(valueFieldId);
                        }}
                    />
                )}

                {isSuggestedMetadataValueListShownPerIndex[index] && filteredSuggestedMetadataValues.length > 0 && (
                    <SuggestedDropDown
                        keyName={'valuesColumn'}
                        languageDirection={languageDirection}
                        items={filteredSuggestedMetadataValues}
                        onItemClick={suggestedValue => {
                            onSuggestedValueSelected(suggestedValue);
                            autoSelectElementWithId(valueFieldId);
                        }}
                    />
                )}
            </>
        );
    };

    return (
        <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto} styles={scrollableContainerStyles}>
            <Stack verticalFill styles={{ root: { width: '100%' } }} tokens={{ childrenGap: 5, padding: '5px 5px 5px 0px' }}>
                {metadataKeys.map((key, index) =>
                    !isInEditMode ? (
                        <Stack key={key} horizontal verticalAlign="center" horizontalAlign="space-between">
                            <Stack horizontal tokens={{ childrenGap: 3 }}>
                                <TruncatedText dir={languageDirection} maxWidth={75} title={key}>
                                    {key}
                                </TruncatedText>
                                {':'}
                                <TruncatedText dir={languageDirection} maxWidth={75} title={metadata[key]}>
                                    {metadata[key]}
                                </TruncatedText>
                            </Stack>

                            {(isInEditMode || showDeleteOption) && (
                                <LuisTooltip content={localize(messages.deleteQuestion)}>
                                    <IconButton
                                        aria-label={localize(messages.deleteQuestion)}
                                        onClick={() => handleMetadataDeletion(key, index)}
                                        iconProps={{ iconName: 'Delete', styles: deleteIconStyles }}
                                    />
                                </LuisTooltip>
                            )}
                        </Stack>
                    ) : (
                        renderMetadataRow({
                            index,
                            key,
                            value: metadata[key],
                            onDelete: () => handleMetadataDeletion(key, index),
                            onValueChange: newVal => {
                                handleMetadataChange(key, key, newVal);
                                updateSuggestedMetadataValueListVisibilityForRow(index, true);
                            },
                            onKeyChange: newKey => {
                                handleMetadataChange(key, newKey, metadata[key]);
                                updateSuggestedMetadataKeyListVisibilityForRow(index, true);
                            },
                            onSuggestedKeySelected: suggestedKey => {
                                handleMetadataChange(key, suggestedKey, '');
                                updateSuggestedMetadataKeyListVisibilityForRow(index, false);
                            },
                            onSuggestedValueSelected: suggestedValue => {
                                handleMetadataChange(key, key, suggestedValue);
                                updateSuggestedMetadataValueListVisibilityForRow(index, false);
                            }
                        })
                    )
                )}

                {isAdditionModeOn &&
                    (areKeysShownInDropDown
                        ? renderMetadataRowWithKeysDropDown({
                              onDelete: handleAdditionRowCanceling,
                              onSelectionChange: handleChangeInAdditionRow
                          })
                        : renderMetadataRow({
                              index: -1,
                              key: newMetaDataKey,
                              isAdditionRow: true,
                              value: newMetaDataValue,
                              onDelete: () => {
                                  handleAdditionRowCanceling();
                                  updateSuggestedMetadataKeyListVisibilityForRow(-1, false);
                                  updateSuggestedMetadataValueListVisibilityForRow(-1, false);
                              },
                              onKeyChange: newKey => {
                                  handleChangeInAdditionRow(newKey, newMetaDataValue);
                                  updateSuggestedMetadataKeyListVisibilityForRow(-1, true);
                              },
                              onValueChange: newValue => {
                                  handleChangeInAdditionRow(newMetaDataKey, newValue);
                                  updateSuggestedMetadataValueListVisibilityForRow(-1, true);
                              },
                              onSuggestedKeySelected: suggestedKey => {
                                  handleChangeInAdditionRow(suggestedKey, '');
                                  updateSuggestedMetadataKeyListVisibilityForRow(-1, false);
                              },
                              onSuggestedValueSelected: suggestedValue => {
                                  handleChangeInAdditionRow(newMetaDataKey, suggestedValue);
                                  updateSuggestedMetadataValueListVisibilityForRow(-1, false);
                              }
                          }))}

                {!readOnly &&
                    (areKeysShownInDropDown ? (
                        <ActionButton
                            id={addButtonId.current}
                            disabled={isAddActionDisabled}
                            text={localize(messages.add)}
                            iconProps={{ iconName: 'Add' }}
                            onClick={handleAddMetadataClick}
                        />
                    ) : (
                        <RBACTooltip
                            displayedUserRole={displayedUserRole}
                            hidden={canWrite || isAddActionDisabled}
                            data-automation-id="addMetadataButtonLanguageToolTip"
                            componentSurroundedByToolTip={
                                <ActionButton
                                    id={addButtonId.current}
                                    text={localize(messages.add)}
                                    iconProps={{ iconName: 'Add' }}
                                    onClick={handleAddMetadataClick}
                                    disabled={!canWrite || isAddActionDisabled}
                                />
                            }
                        />
                    ))}
            </Stack>
        </ScrollablePane>
    );
});
